import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { landingMedia } from './media';

const TYPE_TICK_MS = 26;
const PAUSE_TICKS = 22;

export default function AgentsSection() {
  const { t } = useTranslation('landing');
  const gridRef = useRef<HTMLDivElement>(null);
  const [typing, setTyping] = useState({ message: -1, chars: 0 });

  const messages = [
    t('agents.message1', 'D1 retention reached 42%. The latest changes are working.'),
    t('agents.message2', 'Players are dropping after level 3. I found a difficulty spike. Want me to rebalance it?'),
    t('agents.message3', 'Session time is down 8%. I’d test a new weekend event.'),
  ];

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    let timer: number | undefined;
    const finished = { message: messages.length, chars: 0 };

    const start = () => {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        setTyping(finished);
        return;
      }
      let message = 0;
      let chars = 0;
      let wait = 0;
      setTyping({ message: 0, chars: 0 });
      timer = window.setInterval(() => {
        if (wait > 0) {
          wait--;
          return;
        }
        if (chars < messages[message].length) {
          chars++;
          setTyping({ message, chars });
          return;
        }
        if (message >= messages.length - 1) {
          window.clearInterval(timer);
          setTyping(finished);
          return;
        }
        message++;
        chars = 0;
        wait = PAUSE_TICKS;
        setTyping({ message, chars });
      }, TYPE_TICK_MS);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          start();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats: Array<[string, string, string | undefined]> = [
    [t('agents.stat.retention', 'D1 retention'), '42%', 'ok'],
    [t('agents.stat.dropOff', 'Drop-off'), t('agents.stat.dropOffValue', 'Level 3'), undefined],
    [t('agents.stat.session', 'Session time'), '−8%', 'accent'],
  ];

  return (
    <section className="section" style={{ paddingTop: 'var(--section-y-lg)' }}>
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <h2 className="t-h2" style={{ margin: '0 0 20px' }}>
          {t('agents.titleA', 'A studio of agents')}
          <br />
          <span className="t-accent">{t('agents.titleB', 'working on your game.')}</span>
        </h2>
        <p className="t-lede" style={{ margin: '0 auto', maxWidth: 720 }}>
          {t(
            'agents.lede',
            'Entire’s agents understand your game, players, and performance. They surface opportunities, identify problems, and help build the next version.',
          )}
        </p>
      </div>
      <div
        ref={gridRef}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
          gap: 'clamp(24px, 3vw, 40px)',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          {messages.map((msg, i) => {
            const reached = typing.message >= i;
            const text = typing.message > i ? msg : typing.message === i ? msg.slice(0, typing.chars) : '';
            return (
              <div
                key={i}
                className="agent-card"
                style={{
                  minHeight: 92,
                  borderColor: 'rgba(255, 181, 92, 0.22)',
                  opacity: reached ? 1 : 0.25,
                  transform: reached ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity .4s var(--ease), transform .4s var(--ease)',
                }}
              >
                <div className="label accent">
                  <img src={landingMedia.logo} alt="" />
                  {t('agents.label', 'Entire agent')}
                </div>
                <p>
                  {text}
                  {typing.message === i && <span className="caret" />}
                </p>
              </div>
            );
          })}
        </div>
        <div className="frame glow" style={{ borderRadius: 15 }}>
          <div style={{ borderRadius: 14, overflow: 'hidden', background: 'var(--surface)' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 16px',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <span className="label bright">
                <span className="dot ok" />
                {t('agents.liveGame', 'Live game')}
              </span>
              <span className="label faint">{t('agents.dashboard', 'Dashboard')}</span>
            </div>
            <div
              style={{
                position: 'relative',
                aspectRatio: '16 / 9',
                backgroundImage: `url('${landingMedia.agents.poster}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <video src={landingMedia.agents.video} className="media-fill" preload="metadata" autoPlay muted loop playsInline />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              {stats.map(([label, value, tone], i) => (
                <div key={label} className="stat" style={{ borderRight: i < stats.length - 1 ? '1px solid var(--line)' : undefined }}>
                  <div className="stat-label">{label}</div>
                  <div className={'stat-value' + (tone ? ' ' + tone : '')}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
